// @TODO: this needs to be a protected page...navigating to it right now unauthenticated has no data, but the template is there, and i don't like that
/*
 * Ryan O'Dowd
 * 2022-08-09
 * © Copyright 2022 Oakwood Software Consulting, Inc.  All Rights Reserved.
 */
import {
  Brush,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  Tooltip as RechartsTooltip,
  ReferenceLine,
  ResponsiveContainer, // @TODO: deprecated: replace with `responsive={true}` in any component this is wrapped in (width and height can be moved to children as well...not sure about style though, so just test this)
  XAxis,
  YAxis,
} from 'recharts'; // @TODO: replace with victory-xl
import React, {
  useCallback,
  useEffect,
  useState,
} from 'react';
import {
  fetchAdminAnalytics,
  fetchAllSchools,
  fetchBooks,
  fetchTranslations,
  fetchWebStatsMsSpent,
  fetchWebStatsNumVersesReviewed,
  getRequestHeaders,
  syncBible,
} from '@utils/store/actions';
import Accordion from '@components/common/Accordion';
import Badge from '@components/common/Badge';
import Button from '@components/common/Button';
import EditBibleDialog from './EditBibleDialog';
import IconButton from '@components/common/IconButton';
import NewBibleDialog from './NewBibleDialog';
import NewSchoolDialog from '@website/components/NewSchoolDialog';
import PassagesOfTheDayTable from './PassagesOfTheDayTable';
import PendingBatchesTable from './PendingBatchesTable';
import Popover from '@components/common/Popover';
import SchoolPreview from '@website/components/SchoolPreview';
import Switch from '@components/common/Switch';
import Text from '@components/common/Text';
import Tooltip from '@components/common/Tooltip';
import TranslationManager from './TranslationManager';
import {
  View,
} from 'react-native';
import WebPageStub from '@website/containers/WebPageStub';
import WhatsNewTable from './WhatsNewTable';
import {
  format as dateFnsFormat,
} from 'date-fns';
import fb from '@utils/firebase';
import getStyles from './styles';
import globals from '@utils/globals';
import hooks from '@hooks';
import utilities from '@utils/utilities';
import {
  v4 as uuidv4,
} from 'uuid';

const _renderDateTick = (p, styles) => { // @TODO: make its own component
  const date = new Date(new Date(p.payload.value).getTime() + 1000 * 60 * 60 * 5); // @TODO: add 5 hours for est
  return (
    <g transform={`translate(${p.x},${p.y})`}>
      <text style={styles.tickText} x={0} y={0} dy={10} fill={styles.colors.lightGray}>
        <tspan textAnchor='middle' x='0'>
          {dateFnsFormat(date, 'eee')}
        </tspan>
        {/* @TODO:
        <tspan textAnchor='middle' x='0' dy='14'>
          {dateFnsFormat(date, 'M/d')}
        </tspan>
        */}
      </text>
    </g>
  );
};

const Admin = () => {
  const styles = hooks.useStyles(getStyles);
  const dispatch = hooks.useTypedDispatch();
  const locationState = hooks.useLocationState();

  const [_userData, setUserData]: any = useState([]);
  const [_totalActivityCounts, setTotalActivityCounts]: any = useState([]);
  const [_lessonData, setLessonData] = useState([]);
  const [_isNewSchoolDialogOpen, setIsNewSchoolDialogOpen] = useState(false);
  const [_isNewBibleDialogOpen, setIsNewBibleDialogOpen] = useState(false);
  const [_translationToEdit, setTranslationToEdit] = useState(false);
  const [_translationApiIdBeingSynced, setTranslationApiIdBeingSynced] = useState(null);
  const [_currentBookIdBeingSynced, setCurrentBookIdBeingSynced] = useState(null);
  const [_syncMessage, setSyncMessage] = useState(null);
  const [_currSyncUrl, setCurrSyncUrl] = useState(null);
  const [_forceRefresh, setForceRefresh] = useState(false);
  const [_checkSyncStatusOnly, setCheckSyncStatusOnly] = useState(true);

  const [_hiddenUserLines, setHiddenUserLines] = useState({
    num_mau: true,
    num_wau: true,
    num_dau: false,
    num_new_users: false,
  });
  const [_hiddenProgressLines, setHiddenProgressLines] = useState({
    num_peanuts: true,
    num_lessons: true,
    num_verses_leveled_up: true,
    num_verses: false,
    num_ms_spent: false,
  });

  const whatsNewNotes = hooks.useTypedSelector((state) => state.whatsNewNotes);

  const adminAnalytics = hooks.useTypedSelector((state) => state.adminAnalytics);
  const msSpent = hooks.useTypedSelector((state) => state.webStatsMsSpent);
  const versesReviewed = hooks.useTypedSelector((state) => state.webStatsNumVersesReviewed);

  const schools = hooks.useTypedSelector((state) => state.schools);
  const translations = hooks.useTypedSelector((state) => state.translations);
  const locales = hooks.useTypedSelector((state) => state.locales);

  const apiSyncStagingMetadata = hooks.useTypedSelector((state) => state.apiSyncStagingMetadata);

  const passagesOfTheDay = hooks.useTypedSelector((state) => state.allPassagesOfTheDay);
  const oneDay = 1000 * 60 * 60 * 24;
  const passagesOfTheDayRemaining = Object.values(passagesOfTheDay).filter((p: any) => new Date(p.date_active).getTime() >= (Date.now() - oneDay)).length;

  const [_adminMessage, setAdminMessage] = useState(null);

  const fetchApiSyncStagingMetadataSuccessCallback = useCallback(() => {
    setAdminMessage(null);
  }, []);

  const fetchTranslationInfo = useCallback(() => {
    dispatch(fetchTranslations());
    setAdminMessage('API sync metadata loading...');
    utilities.fetchApiSyncStagingMetadataOutsideOfReduxApiMiddleware(() => fetchApiSyncStagingMetadataSuccessCallback());
  }, [dispatch, fetchApiSyncStagingMetadataSuccessCallback]);

  useEffect(() => {
    const _fetchData = async () => {
      try {
        const response = await fetch(`${globals.apiUrl}/admin/analytics/data`, {
          headers: await getRequestHeaders(), // @TODO: is this how we do auth for fetch api?
        });
        const payload = await response.json();
        setUserData(payload.user_data);
        setTotalActivityCounts(payload.total_user_activity);
        setLessonData(payload.lesson_data);
      } catch (error) {
        fb.recordError(fb.crashlytics, error);
      }
    };

    _fetchData();

    dispatch(fetchBooks());
    dispatch(fetchAdminAnalytics());
    dispatch(fetchWebStatsMsSpent()); // @TODO: this shouldn't be a live stat...maybe update every month or so, but i don't want people calling this api on every page load
    dispatch(fetchWebStatsNumVersesReviewed()); // @TODO: this shouldn't be a live stat...maybe update every month or so, but i don't want people calling this api on every page load
    dispatch(fetchAllSchools());

    fetchTranslationInfo();
  }, [dispatch, fetchTranslationInfo]);

  const _syncBible = (translationApiId, version, batchId, checkStatusOnly, autoAcceptNew = false, forceRefresh = false) => {
    setTranslationApiIdBeingSynced(translationApiId);
    setCurrentBookIdBeingSynced(1);
    setSyncMessage(`Starting sync of ${translationApiId}`);
    dispatch(syncBible(
      translationApiId,
      version,
      batchId,
      (isError, bookId, message, nextUrl) => {
        if (nextUrl) {
          setCurrSyncUrl(nextUrl);
        }

        if (bookId) {
          setCurrentBookIdBeingSynced(+bookId);
        } else {
          // syncing has finished (don't know if succesful or not)
          setTranslationApiIdBeingSynced(null);
          setCurrentBookIdBeingSynced(null);
          if (!isError) {
            // only fetch dffs and reset url on successful finish
            fetchTranslationInfo();
            setCurrSyncUrl(null);
          }
        }

        setSyncMessage(message);
      },
      (errorMessage) => {
        setTranslationApiIdBeingSynced(null);
        setSyncMessage(errorMessage);
      },
      checkStatusOnly,
      _currSyncUrl,
      autoAcceptNew,
      forceRefresh,
    ));
  };

  return (
    <WebPageStub adminMessage={_adminMessage}>
      <View style={styles.container}>
        <View style={styles.section}>
          <View style={styles.sectionInner}>
            <Text style={styles.sectionTitle}>Analytics</Text>

            <Accordion
              defaultOpen={locationState.showUserStats}
              title='User stats'
            >
              {!_userData.length
                ? <p>Loading...</p>// @TODO: prettier
                : (
                  <ResponsiveContainer
                    style={styles.chartWrapper}
                    width='100%'
                    height={400}
                  >
                    <LineChart // @TODO: can abstract this to be reusable
                      style={styles.chart}
                      data={_userData}
                      margin={{
                        top: 8,
                        right: 8,
                        left: 8,
                        bottom: 8,
                      }}
                    >
                      <YAxis width={32} yAxisId='numNewUsers' stroke={styles.colors.primary} />
                      <YAxis width={24} yAxisId='numXau' stroke={styles.colors.white} />
                      <XAxis
                        dataKey='date'
                        interval={1}
                        tick={(p) => _renderDateTick(p, styles)}
                      />
                      <RechartsTooltip
                        contentStyle={styles.tooltip}
                        formatter={(num, __payload) => {
                          return utilities.humanReadableScore(num);
                        }}
                        labelFormatter={(label, __payload) => {
                          return <Text style={styles.tooltipDate}>{dateFnsFormat(new Date(new Date(label).getTime() + 1000 * 60 * 60 * 5), 'eee M/d')}</Text>; // @TODO: add 5 hours to make it est
                        }}
                      />
                      <CartesianGrid strokeDasharray='4' stroke={styles.colors.primaryDark} />
                      <Legend
                        onClick={(e) => {
                          setHiddenUserLines((curr) => {
                            return {
                              ..._hiddenUserLines,
                              [e.dataKey as any]: !curr[e.dataKey as any],
                            };
                          });
                        }}
                      />
                      {/* @TODO: add average lines for each metric */}
                      <Line type='monotone' activeDot={{r: 6}} hide={_hiddenUserLines.num_new_users} yAxisId='numNewUsers' dataKey='num_new_users' stroke={styles.colors.primary} name='New users' />
                      <Line type='monotone' activeDot={{r: 6}} hide={_hiddenUserLines.num_dau} yAxisId='numXau' dataKey='num_dau' stroke={styles.colors.accent} name='DAU' />
                      <Line type='monotone' activeDot={{r: 6}} hide={_hiddenUserLines.num_wau} yAxisId='numXau' dataKey='num_wau' stroke={styles.colors.complete} name='WAU' />
                      <Line type='monotone' activeDot={{r: 6}} hide={_hiddenUserLines.num_mau} yAxisId='numXau' dataKey='num_mau' stroke={styles.colors.alert} name='MAU' />
                      {whatsNewNotes.map((note) => <ReferenceLine key={note.id} label={note.app_version} yAxisId='numNewUsers' stroke={styles.colors.qqq} x={note.release_date} />)}
                      <Brush
                        dataKey='date'
                        height={10}
                        // @TODO: y={10}
                        stroke={styles.colors.primaryDark}
                        startIndex={_userData.length - 28}// @TODO: no longer working in v3
                        travellerWidth={20}
                        tickFormatter={(t) => {
                          const date = new Date(new Date(t).getTime() + 1000 * 60 * 60 * 5); // @TODO: add 5 hours for est
                          return dateFnsFormat(date, 'M/d');
                        }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                )
              }
              <Text style={styles.totalsHeader}>Lifetime totals</Text>
              <View style={styles.totalsWrapper}>
                <View style={styles.statWrapper}>
                  <Text style={styles.statLabel}>Users:</Text>
                  <Text style={styles.statText}>{adminAnalytics.num_users?.toLocaleString()}</Text>
                </View>
              </View>
              <Text style={styles.totalsHeader}>Current active-user counts (sign-in, not lesson as above)</Text>
              <View style={styles.totalsWrapper}>
                <View style={styles.statWrapper}>
                  <Text style={styles.statLabel}>DAU:</Text>
                  <Text style={styles.statText}>{_totalActivityCounts?.total_dau?.toLocaleString()}</Text>
                </View>
                <View style={styles.statWrapper}>
                  <Text style={styles.statLabel}>WAU:</Text>
                  <Text style={styles.statText}>{_totalActivityCounts?.total_wau?.toLocaleString()}</Text>
                </View>
                <View style={styles.statWrapper}>
                  <Text style={styles.statLabel}>MAU:</Text>
                  <Text style={styles.statText}>{_totalActivityCounts?.total_mau?.toLocaleString()}</Text>
                </View>
                <View style={styles.statWrapper}>
                  <Text
                    style={styles.statLabel}
                    isLink={true}
                    href='https://console.firebase.google.com/u/0/project/wrinkly-prod/analytics/app/android:com.oakwoodsc.wrinkly/overview/reports~2Fdashboard%3Fr%3Dfirebase-overview&fpn%3D182142003913'
                  >
                    See more
                  </Text>
                </View>
              </View>
              {/* @TODO:
              <p>TODO: week/month/year/custom range views</p>
              */}
            </Accordion>

            <Accordion
              defaultOpen={locationState.showProgressStats}
              title='Progress stats'
            >
              {!_lessonData.length
                ? <p>Loading...</p>// @TODO: prettier
                : (
                  <ResponsiveContainer
                    style={styles.chartWrapper}
                    width='100%'
                    height={400}
                  >
                    <LineChart
                      style={styles.chart}
                      width={900}// @TODO: should be 100%
                      height={400}
                      data={_lessonData}
                      margin={{
                        top: 8,
                        right: 8,
                        left: 8,
                        bottom: 8,
                      }}
                    >
                      {/* @TODO:
                      <YAxis width={40} yAxisId='wrinkles' stroke={styles.colors.complete} />
                      <YAxis type='number' width={40} yAxisId='msSpent' stroke={styles.colors.primary} />
                      <YAxis type='number' width={40} yAxisId='verses' stroke={styles.colors.complete} />
                      <YAxis type='number' width={40} yAxisId='lessons' stroke={styles.colors.alert} />
                      <YAxis type='number' width={40} yAxisId='peanuts' stroke={styles.colors.accent} />
                      */}
                      <XAxis
                        dataKey='date'
                        interval={1}
                        tick={(p) => _renderDateTick(p, styles)}
                      />
                      <RechartsTooltip
                        contentStyle={styles.tooltip}
                        formatter={(num, label) => {
                          if (label === 'Time') {
                            return utilities.humanReadableTimeDashboard(num);
                          }
                          return utilities.humanReadableScore(num);
                        }}
                        labelFormatter={(label, __payload) => {
                          return <Text style={styles.tooltipDate}>{dateFnsFormat(new Date(new Date(label).getTime() + 1000 * 60 * 60 * 5), 'eee M/d')}</Text>; // @TODO: off by one add 5 hours to make it est
                        }}
                      />
                      <CartesianGrid strokeDasharray='4' stroke={styles.colors.primaryDark} />
                      <Legend
                        onClick={(e) => {
                          setHiddenProgressLines((curr) => {
                            return {
                              ..._hiddenProgressLines,
                              [e.dataKey as any]: !curr[e.dataKey as any],
                            };
                          });
                        }}
                      />
                      {/* @TODO: add average lines for each metric */}
                      <Line type='monotone' activeDot={{r: 6}} hide={_hiddenProgressLines.num_peanuts} yAxisId='peanuts' name='Peanuts' dataKey='num_peanuts' stroke={styles.colors.accent} />
                      <Line type='monotone' activeDot={{r: 6}} hide={_hiddenProgressLines.num_lessons} yAxisId='lessons' name='Lessons' dataKey='num_lessons' stroke={styles.colors.alert} />
                      <Line type='monotone' activeDot={{r: 6}} hide={_hiddenProgressLines.num_verses} yAxisId='verses' name='Verses Reviewed' dataKey='num_verses' stroke={styles.colors.complete} />
                      <Line type='monotone' activeDot={{r: 6}} hide={_hiddenProgressLines.num_verses_leveled_up} yAxisId='versesLeveledUp' name='Verses Leveled Up' dataKey='num_verses_leveled_up' stroke={styles.colors.qqq} />
                      <Line type='monotone' activeDot={{r: 6}} hide={_hiddenProgressLines.num_ms_spent} yAxisId='msSpent' name='Time' dataKey='num_ms_spent' stroke={styles.colors.primary} />
                      {whatsNewNotes.map((note) => <ReferenceLine key={note.id} label={note.app_version} yAxisId='msSpent' stroke={styles.colors.qqq} x={note.release_date} />)}
                      <Brush
                        dataKey='date'
                        height={10}
                        // @TODO: y={10}
                        stroke={styles.colors.primaryDark}
                        startIndex={_lessonData.length - 28}// @TODO: no longer working in v3
                        travellerWidth={20}
                        tickFormatter={(t) => {
                          const date = new Date(new Date(t).getTime() + 1000 * 60 * 60 * 5); // @TODO: add 5 hours for est
                          return dateFnsFormat(date, 'M/d');
                        }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                )
              }
              <Text style={styles.totalsHeader}>Lifetime totals</Text>
              <View style={styles.totalsWrapper}>
                <View style={styles.statWrapper}>
                  <Text style={styles.statLabel}>Peanuts earned:</Text>
                  <Tooltip title={(+adminAnalytics.num_peanuts)?.toLocaleString()}>
                    <Text style={styles.statText}>{utilities.humanReadableNumber(adminAnalytics.num_peanuts)}</Text>
                  </Tooltip>
                </View>
                <View style={styles.statWrapper}>
                  <Text style={styles.statLabel}>Lessons completed:</Text>
                  <Tooltip title={(+adminAnalytics.num_lessons)?.toLocaleString()}>
                    <Text style={styles.statText}>{utilities.humanReadableNumber(adminAnalytics.num_lessons)}</Text>
                  </Tooltip>
                  {/* @TODO: drill down for difficulty */}
                </View>
                <View style={styles.statWrapper}>
                  <Text style={styles.statLabel}>Questions asked:</Text>
                  <Tooltip title={(+adminAnalytics.num_questions_asked)?.toLocaleString()}>
                    <Text style={styles.statText}>{utilities.humanReadableNumber(adminAnalytics.num_questions_asked)}</Text>
                  </Tooltip>
                  {/* @TODO: option to drill down for more specifics...like type of quesitons for example */}
                </View>
                <View style={styles.statWrapper}>
                  <Text style={styles.statLabel}>Questions skipped:</Text>
                  <Tooltip title={(+adminAnalytics.num_skipped_questions)?.toLocaleString()}>
                    <Text style={styles.statText}>{utilities.humanReadableNumber(adminAnalytics.num_skipped_questions)}</Text>
                  </Tooltip>
                </View>
                <View style={styles.statWrapper}>
                  <Text style={styles.statLabel}>Wrinkles earned:</Text>
                  {/* @TODO: tooltip doesn't work on web mobile...this is a hacky fix for now to use popover
                  <Tooltip title={(+adminAnalytics.num_wrinkles)?.toLocaleString()}>
                    <Text style={styles.statText}>{utilities.humanReadableNumber(adminAnalytics.num_wrinkles)}</Text>
                    // @TODO: add other stats here as well
                  </Tooltip>
                  */}
                  <Popover.Wrapper// @TODO: this is janky on initial render
                    popover={
                      <Popover.Inner>
                        <Popover.Text title={true}>Wrinkles earned</Popover.Text>
                        <Popover.Text>{(+adminAnalytics.num_wrinkles_earned)?.toLocaleString()}</Popover.Text>
                        <Popover.Text title={true}>Wrinkles in progress</Popover.Text>
                        <Popover.Text>{(+adminAnalytics.num_wrinkles_in_progress)?.toLocaleString()}</Popover.Text>
                        <Popover.Text title={true}>Level-ups achieved</Popover.Text>
                        <Popover.Text>{(+adminAnalytics.num_level_ups)?.toLocaleString()}</Popover.Text>
                        <Popover.Text title={true}>Verses recited successfully</Popover.Text>
                        <Popover.Text>{(+adminAnalytics.num_passed_verses)?.toLocaleString()}</Popover.Text>
                      </Popover.Inner>
                    }
                  >
                    <Text style={styles.statText}>{utilities.humanReadableNumber(adminAnalytics.num_wrinkles_earned)}</Text>
                  </Popover.Wrapper>
                </View>
                <View style={styles.statWrapper}>
                  <Text style={styles.statLabel}>Verses reviewed:</Text>
                  {/* @TODO: tooltip doesn't work on web mobile...this is a hacky fix for now to use popover
                  <Tooltip title={(+versesReviewed)?.toLocaleString()}>
                    <Text style={styles.statText}>{utilities.humanReadableNumber(versesReviewed)}</Text>
                  </Tooltip>
                  */}
                  <Popover.Wrapper// @TODO: this is janky on initial render
                    popover={
                      <Popover.Inner>
                        <Popover.Text title={true}>Verses reviewed</Popover.Text>
                        <Popover.Text>{(+versesReviewed)?.toLocaleString()}</Popover.Text>
                      </Popover.Inner>
                    }
                  >
                    <Text style={styles.statText}>{utilities.humanReadableNumber(versesReviewed)}</Text>
                  </Popover.Wrapper>
                </View>
                <View style={styles.statWrapper}>
                  <Text style={styles.statLabel}>Time spent:</Text>
                  <Tooltip title={`${Math.floor((+msSpent) / 3600000)}:${Math.floor((+msSpent) / 60000 % 60)}:${Math.floor((+msSpent) / 1000 % 60)}.${(+msSpent) % 1000}`}>
                    <Text style={styles.statText}>{utilities.humanReadableTime(msSpent)}</Text>
                  </Tooltip>
                </View>
              </View>
              {/* @TODO:
              <p>TODO: week/month/year/custom range views</p>
              */}
            </Accordion>
          </View>
        </View>

        <View style={styles.section}>
          <View style={styles.sectionInner}>
            <Text style={styles.sectionTitle}>Administration</Text>

            <Accordion
              title='Manage Passages of the Day'
              hasBadge={passagesOfTheDayRemaining <= 14}
              badgeText={`${passagesOfTheDayRemaining}`}
            >
              <PassagesOfTheDayTable />
            </Accordion>

            <Accordion title={`Manage "What's New?" notes`}>
              <WhatsNewTable />
            </Accordion>

            {/* @TODO:
            <Accordion title='Review reported tests'>
              <p>TODO</p>
            </Accordion>
            */}
          </View>
        </View>

        <View style={styles.section}>
          <View style={styles.sectionInner}>
            <View style={styles.titleAndAction}>
              <Text style={styles.sectionTitle}>Bibles<Badge color={styles.colors.accent} top={styles.globalScale * -8} right={styles.globalScale * -24}>{!!apiSyncStagingMetadata?.translations && Object.keys(apiSyncStagingMetadata.translations).length}</Badge></Text>
              <Button
                theme={styles.colors.accent}
                onPress={() => setIsNewBibleDialogOpen(true)}
              >
                New Bible
              </Button>
            </View>

            {!!apiSyncStagingMetadata?.all_notes?.length &&
              <TranslationManager
                type='Miscellaneous notes'
                diffs={apiSyncStagingMetadata?.all_notes}
                skipDiff={true}
                fetchApiSyncStagingMetadataSuccessCallback={fetchApiSyncStagingMetadataSuccessCallback}
              />
            }

            {!!apiSyncStagingMetadata?.pending_batches?.length &&
              <PendingBatchesTable
                batches={apiSyncStagingMetadata?.pending_batches}
                fetchApiSyncStagingMetadataSuccessCallback={fetchApiSyncStagingMetadataSuccessCallback}
              />
            }

            {translations.map((translation) => {// @TODO: should disabled ones be displayed?
              const diffs = apiSyncStagingMetadata?.translations?.[translation.id] || {}; // @TODO: shouldn't need {} (all translations will be present)
              const numDiffs = Object.values(diffs).flat()?.length || 0;

              const thisBatchId = Object.values(diffs).map((ds) => ds[0]?.batch_id).find((bid) => bid);

              const thisTranslationIsBeingSynced = _translationApiIdBeingSynced === translation.api_id;

              // sync if it's been 3 weeks since the last check
              let thisTranslationNeedsToBeSynced = !!translation.api_id && new Date(translation.last_synced_at) < new Date(new Date().getTime() - (1000 * 60 * 60 * 24 * 7 * 3));
              const thisTranslationNeedsToBeForceSynced = new Date(translation.last_full_sync_at) < new Date(new Date().getTime() - (1000 * 60 * 60 * 24 * 7 * 13));
              if (['78a9f6124f344018-01', // niv
                   'da0947e25c9636bb-01', // hfa
                   '01c25b8715dbb632-01', // nvi
                   '0a8c5612ed1cb866-01', // esv
                  ].includes(translation.api_id)) {
                // @TODO: remove this condition when everything is moved to the new API
                thisTranslationNeedsToBeSynced = false;
              }

              let thisTranslationSyncingIsPaused = false;
              const pausedTranslationIds = [
                1, // niv1984
                8, // hfa
                11, // csb
              ];
              if (pausedTranslationIds.includes(translation.id)) {
                thisTranslationSyncingIsPaused = true;
              }

              return (
                <View key={translation.id}>
                  <Accordion
                    title={translation.name}
                    subtitle={translation.abbreviation}
                    hasBadge={(numDiffs > 0) || thisTranslationNeedsToBeSynced || thisTranslationSyncingIsPaused}
                    badgeText={thisTranslationSyncingIsPaused ? `${translation.abbreviation} syncing is paused` : (thisTranslationNeedsToBeSynced && !numDiffs) ? 'Sync me' : `${numDiffs}`}
                    badgeColor={thisTranslationSyncingIsPaused && styles.colors.primary}
                    leftItem={
                      <View>
                        <IconButton
                          style={styles.editButton}
                          icon='pencil'
                          size={24}
                          iconColor={styles.colors.primaryExtraLight}
                          onPress={() => setTranslationToEdit(translation)}
                        />
                      </View>
                    }
                  >
                    {!!diffs.translation_metadata?.length &&
                      <TranslationManager
                        type='Translation metadata'
                        diffs={diffs.translation_metadata}
                        fetchApiSyncStagingMetadataSuccessCallback={fetchApiSyncStagingMetadataSuccessCallback}
                      />
                    }
                    {!!diffs.verse_texts?.length &&
                      <TranslationManager
                        type='Verse texts'
                        diffs={diffs.verse_texts}
                        fetchApiSyncStagingMetadataSuccessCallback={fetchApiSyncStagingMetadataSuccessCallback}
                      />
                    }
                    {!!diffs.footnotes?.length &&
                      <TranslationManager
                        type='Footnotes'
                        diffs={diffs.footnotes}
                        fetchApiSyncStagingMetadataSuccessCallback={fetchApiSyncStagingMetadataSuccessCallback}
                      />
                    }
                    {!!diffs.book_names?.length &&
                      <TranslationManager
                        type='Book names'
                        diffs={diffs.book_names}
                        fetchApiSyncStagingMetadataSuccessCallback={fetchApiSyncStagingMetadataSuccessCallback}
                      />
                    }

                    {/* @TODO: show last sync finished at timestamp (max synced_at for this translation) */}
                    {!!translation.api_id &&
                      <View style={styles.syncNowButtonWrapper}>
                        {!!_syncMessage && <Text style={styles.syncMessageText}>{_syncMessage}</Text>}
                        <Button
                          theme={styles.colors.alert}
                          onPress={() => {
                            const batchId = thisBatchId || uuidv4();
                            _syncBible(
                              translation.api_id,
                              utilities.lexigraphicToSemver(translation.available_in_version_lexigraphic),
                              batchId,
                              _checkSyncStatusOnly,
                              false,
                              _forceRefresh,
                            );
                          }}
                          disabled={!!_translationApiIdBeingSynced}// prevent simultaneous syncing
                          loading={thisTranslationIsBeingSynced}
                        >
                          {thisTranslationIsBeingSynced ? `Syncing book ${_currentBookIdBeingSynced} / 66` : (_currSyncUrl ? 'Resume sync' : (_checkSyncStatusOnly ? 'Check for changes' : 'Sync now'))}
                        </Button>
                        <Switch
                          isChecked={_checkSyncStatusOnly}
                          onValueChange={() => setCheckSyncStatusOnly((curr) => !curr)}
                          disabled={!!_translationApiIdBeingSynced}
                          label='Check sync status only'
                          labelColor={styles.colors.white}
                        />
                        <Switch
                          isChecked={_forceRefresh}
                          onValueChange={() => setForceRefresh((curr) => !curr)}
                          disabled={!!_translationApiIdBeingSynced}
                          label='Force refresh'
                          labelColor={styles.colors.white}
                        />
                      </View>
                    }

                    <Text style={styles.totalsHeader}>Translation info</Text>
                    <View style={styles.totalsWrapper}>
                      <View style={styles.statWrapper}>
                        <Text style={styles.statLabel}>ID:</Text>
                        <Text style={styles.statText}>{translation.id}</Text>
                      </View>
                      <View style={styles.statWrapper}>
                        <Text style={styles.statLabel}>Created at:</Text>
                        <Tooltip title={dateFnsFormat(new Date(translation.created_at), 'h:mm a')}><Text style={styles.statText}>{dateFnsFormat(new Date(translation.created_at), 'MMM d, yyyy')}</Text></Tooltip>
                      </View>
                      <View style={styles.statWrapper}>
                        <Text style={styles.statLabel}>First available in version:</Text>
                        <Tooltip title={'TODO: date this version was released'}><Text style={styles.statText}>{utilities.lexigraphicToSemver(translation.available_in_version_lexigraphic)}</Text></Tooltip>
                      </View>
                      <View style={styles.statWrapper}>
                        <Text style={styles.statLabel}>Last synced at:</Text>
                        <Tooltip title={dateFnsFormat(new Date(translation.last_synced_at), 'h:mm a')}><Text style={styles.statText}>{dateFnsFormat(new Date(translation.last_synced_at), 'MMM d, yyyy')}</Text></Tooltip>
                      </View>
                      <View style={styles.statWrapper}>
                        <Text style={styles.statLabel}>Last full synced at:</Text>
                        <Tooltip title={dateFnsFormat(new Date(translation.last_full_sync_at), 'h:mm a')}><Text style={styles.statText}>{dateFnsFormat(new Date(translation.last_full_sync_at), 'MMM d, yyyy')}</Text></Tooltip>
                      </View>
                      <View style={styles.statWrapper}>
                        <Text style={styles.statLabel}>Locale:</Text>
                        <Text style={styles.statText}>{locales[translation.locale_id]?.language_name}</Text>
                      </View>
                      <View style={styles.statWrapper}>
                        <Text style={styles.statLabel}>API ID:</Text>{/* @TODO: need this to be displayed? */}
                        <Text style={styles.statText}>{translation.api_id}</Text>
                      </View>
                      {/* @TODO:
                      <View style={styles.statWrapper}>
                        <Text style={styles.statLabel}>Copyright short:</Text>
                        <Text style={styles.statText}>{translation.copyright_short}</Text>{/* @TODO: show in a dialog on click /}
                      </View>
                      <View style={styles.statWrapper}>
                        <Text style={styles.statLabel}>Copyright long:</Text>
                        <Text style={styles.statText}>{translation.copyright_long}</Text>{/* @TODO: show in a dialog on click /}
                      </View>
                      <View style={styles.statWrapper}>
                        <Text style={styles.statLabel}>Info:</Text>
                        <Text style={styles.statText}>{translation.info}</Text>{/* @TODO: show in a dialog on click /}
                      </View>
                      */}
                      <View style={styles.statWrapper}>
                        <Text style={styles.statLabel}>URL:</Text>
                        <Text style={styles.statText}>{translation.url}</Text>{/* @TODO: make this linkable */}
                      </View>
                    </View>
                  </Accordion>
                  {(thisTranslationNeedsToBeForceSynced && !thisTranslationSyncingIsPaused) && <Badge color={styles.colors.complete}>Force sync me</Badge>}{/* @TODO: i18n */}
                </View>
              );
            })}
          </View>
        </View>

        <View style={styles.section}>
          <View style={styles.sectionInner}>
            <View style={styles.titleAndAction}>
              <Text style={styles.sectionTitle}>Schools</Text>
              {/* @TODO: with self registration available, does this need to be an option for wrinkly admins?
              <Button
                theme={styles.colors.accent}
                onPress={() => setIsNewSchoolDialogOpen(true)}
              >
                New school
              </Button>
              */}
            </View>

            {schools.map((school) => {
              return (
                <SchoolPreview
                  key={school.id}
                  title={school.name}
                  school={school}
                  isWrinklyAdmin={true}
                />
              );
            })}
          </View>
        </View>

      </View>

      {_isNewSchoolDialogOpen && <NewSchoolDialog isAdmin={true} isOpen={_isNewSchoolDialogOpen} onClose={() => setIsNewSchoolDialogOpen(false)} />}
      {_translationToEdit && <EditBibleDialog onClose={() => setTranslationToEdit(null)} translation={_translationToEdit} fetchApiSyncStagingMetadataSuccessCallback={fetchApiSyncStagingMetadataSuccessCallback} />}
      {_isNewBibleDialogOpen &&
        <NewBibleDialog
          onClose={() => setIsNewBibleDialogOpen(false)}
          syncMessage={_syncMessage}
          syncBible={_syncBible}
          setSyncMessage={setSyncMessage}
          currSyncUrl={_currSyncUrl}
          isSyncing={!!_translationApiIdBeingSynced}
        />
      }
    </WebPageStub>
  );
};

export default Admin;
